import { type ReactNode } from "react"
import type { VariantProps } from "class-variance-authority"
import { ArrowUpDown } from "lucide-react"
import { Link } from "react-router-dom"
import {
  type CellContext,
  type ColumnDef,
  type HeaderContext,
} from "@tanstack/react-table"
import { Button, buttonVariants } from "@components/ui/button"
import { cn } from "@components/ui/lib/utils"
import { Checkbox } from "@components/ui/checkbox"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/ui/tooltip"
import { formatCurrency, formatDate } from "@utils/formatters"

export const CurrencyCell = <T,>({ getValue }: CellContext<T, unknown>) => {
  const value = getValue()
  const formattedValue = formatCurrency(value)

  return <div className="tw:text-right">{formattedValue}</div>
}

export const DateCell = <T,>({ getValue }: CellContext<T, unknown>) => {
  const rawDate = getValue() as string | null
  const formattedDate = formatDate(rawDate)

  return (
    <div className="tw:text-left tw:font-medium">{formattedDate ?? "-"}</div>
  )
}

export const TruncatedTooltipCell = <T,>({
  getValue,
}: CellContext<T, unknown>) => {
  const value = getValue()
  const displayValue = value ? String(value) : "-"

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="tw:max-w-3xs tw:truncate">
          <span>{displayValue}</span>
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" className="tw:max-w-sm">
        <span>{displayValue}</span>
      </TooltipContent>
    </Tooltip>
  )
}

export const ButtonCell = <T,>({
  row,
  label,
  icon,
  classes,
  variant,
  onClick,
}: CellContext<T, unknown> & {
  label: string
  icon?: ReactNode
  classes?: string
  variant?: VariantProps<typeof buttonVariants>["variant"]
  onClick: (row: T) => void
}) => {
  const rowData = row.original

  return (
    <Button
      onClick={() => onClick(rowData)}
      className={cn("tw:flex tw:flex-row tw:gap-2", classes)}
      variant={variant ?? "default"}
    >
      {icon && <span>{icon}</span>}
      <span>{label}</span>
    </Button>
  )
}

export const LinkCell = ({
  children,
  className,
  to,
}: {
  children: ReactNode
  className?: string
  to: string
}) => {
  return (
    <Button asChild variant="link">
      <Link to={to} className={cn(className)}>
        {children}
      </Link>
    </Button>
  )
}

export const SortingHeader = <T,>({
  column,
  label,
}: HeaderContext<T, unknown> & { label: string }) => (
  <Button
    variant="ghost"
    size="xs"
    className="tw:font-bold"
    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
  >
    {label}
    <ArrowUpDown />
  </Button>
)

export const selectColumn = <T,>(): ColumnDef<T> => ({
  id: "select",
  header: ({ table }) => (
    <Checkbox
      checked={
        table.getIsAllPageRowsSelected() ||
        (table.getIsSomePageRowsSelected() && "indeterminate")
      }
      onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
      aria-label="Select all"
    />
  ),
  cell: ({ row }) => (
    <Checkbox
      checked={row.getIsSelected()}
      onCheckedChange={(value) => row.toggleSelected(!!value)}
      aria-label="Select row"
    />
  ),
  enableSorting: false,
  enableHiding: false,
  meta: {
    disableColumnOrdering: true,
  },
})
